import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Button,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { trainingService } from '../../services/trainingService';
import { formatDateDisplay } from '../../utils/dateUtils';
import TrainingSignForm from '../../components/TrainingSignForm';
import TrainingPdfViewer from '../../components/TrainingPdfViewer';
import { useAuth } from '../../hooks/useAuth';
import axios from 'axios';

const buildFieldValuesFromCompletion = (fields, fieldCompletions, fieldImageUrls) => {
  const values = {};
  for (const field of fields || []) {
    const fc = (fieldCompletions || []).find((c) => c.training_document_field_id === field.id);
    values[field.id] = {
      checked: !!fc?.checked,
      dateValue: fc?.date_value ? String(fc.date_value).slice(0, 10) : null,
      signatureImage: fieldImageUrls[field.id] || null,
    };
  }
  return values;
};

const EmployeeTrainingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [completion, setCompletion] = useState(null);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [fieldValues, setFieldValues] = useState({});
  const [signatureImage, setSignatureImage] = useState(null);
  const [signatureText, setSignatureText] = useState(null);
  const [employeeSigUrl, setEmployeeSigUrl] = useState('');
  const [supervisorSigUrl, setSupervisorSigUrl] = useState('');
  const [pdfBlob, setPdfBlob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await trainingService.getCompletion(id);
      const data = res.data;
      setCompletion(data);
      const checked = new Set((data.itemCompletions || []).map((ic) => ic.training_document_item_id));
      setCheckedIds(checked);

      const fieldImageUrls = {};
      for (const fc of data.fieldCompletions || []) {
        if (fc.signature_image && fc.training_document_field_id) {
          try {
            const imgRes = await axios.get(
              trainingService.getFieldSignatureImageUrl(id, fc.training_document_field_id),
              { responseType: 'blob' }
            );
            fieldImageUrls[fc.training_document_field_id] = URL.createObjectURL(imgRes.data);
          } catch {
            fieldImageUrls[fc.training_document_field_id] = null;
          }
        }
      }
      setFieldValues(buildFieldValuesFromCompletion(data.fields, data.fieldCompletions, fieldImageUrls));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load training');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!completion) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const blob = await trainingService.downloadCompletionPdf(id);
        if (!cancelled) setPdfBlob(blob);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || err.message || 'Failed to load PDF');
      }
    })();
    return () => { cancelled = true; };
  }, [id, completion]);

  useEffect(() => {
    if (!completion) return;
    (async () => {
      if (completion.employee_signature_image) {
        try {
          const res = await axios.get(trainingService.getSignatureImageUrl(id, 'employee'), { responseType: 'blob' });
          const url = URL.createObjectURL(res.data);
          setEmployeeSigUrl(url);
        } catch {
          setEmployeeSigUrl('');
        }
      }
      if (completion.supervisor_signature_image) {
        try {
          const res = await axios.get(trainingService.getSignatureImageUrl(id, 'supervisor'), { responseType: 'blob' });
          const url = URL.createObjectURL(res.data);
          setSupervisorSigUrl(url);
        } catch {
          setSupervisorSigUrl('');
        }
      }
    })();
  }, [completion, id]);

  const isOwnRecord = completion && user && completion.employee_id === user.id;
  const canSignEmployee = !!isOwnRecord && completion?.status === 'pending';
  const canSignSupervisor = completion && !isOwnRecord && completion.status === 'employee_signed';
  const isSigning = canSignEmployee || canSignSupervisor;
  const activeRole = canSignSupervisor ? 'supervisor' : 'employee';

  const toggleItem = (itemId) => {
    if (!canSignEmployee) return;
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const handleFieldChange = (fieldId, update) => {
    if (!isSigning) return;
    setFieldValues((prev) => ({
      ...prev,
      [fieldId]: { ...prev[fieldId], ...update },
    }));
  };

  const defaultSignName = useMemo(() => {
    const parts = [user?.first_name, user?.last_name].filter(Boolean);
    return parts.join(' ').trim();
  }, [user]);

  const buildFieldCompletions = () => {
    const roleByFieldId = new Map(
      (completion?.fields || []).map((f) => [f.id, f.signer_role || 'employee'])
    );
    const isFreshSignature = (img) => typeof img === 'string' && img.startsWith('data:');
    return Object.entries(fieldValues)
      // Only submit fields for the role currently signing so we never re-send
      // another signer's already-persisted signature (loaded as a blob: URL).
      .filter(([fieldId]) => (roleByFieldId.get(parseInt(fieldId, 10)) || 'employee') === activeRole)
      .map(([fieldId, value]) => ({
        fieldId: parseInt(fieldId, 10),
        ...(value.checked !== undefined ? { checked: value.checked } : {}),
        ...(value.dateValue ? { dateValue: value.dateValue } : {}),
        // Only send freshly captured signatures (data URLs); skip persisted blob URLs.
        ...(isFreshSignature(value.signatureImage) ? { signatureImage: value.signatureImage } : {}),
      }));
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      const fieldCompletions = buildFieldCompletions();
      if (canSignSupervisor) {
        await trainingService.supervisorSign(id, {
          signatureImage,
          signatureText: signatureText || undefined,
          fieldCompletions,
        });
        setSuccess('Sign-off submitted successfully.');
      } else {
        await trainingService.employeeSign(id, {
          signatureImage,
          signatureText: signatureText || undefined,
          checkedItemIds: Array.from(checkedIds),
          fieldCompletions,
        });
        setSuccess('Training signed successfully.');
      }
      setSignatureImage(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit signature');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!completion) {
    return <Alert severity="error">Training record not found.</Alert>;
  }

  const hasPdfFields = (completion.fields || []).length > 0;
  const hasChecklist = (completion.items || []).length > 0;
  const supervisorFieldCount = (completion.fields || []).filter((f) => (f.signer_role || 'employee') === 'supervisor').length;
  const isComplete = completion.status === 'supervisor_signed';

  const handleDownloadCompleted = async () => {
    setError('');
    try {
      const blob = await trainingService.downloadCompletedPdf(id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to download completed document');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Button size="small" onClick={() => navigate(-1)}>
          Back
        </Button>
        {isComplete && (
          <Button size="small" variant="outlined" onClick={handleDownloadCompleted}>
            Download completed PDF
          </Button>
        )}
      </Box>
      <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 1 }}>
        {completion.document_title}
      </Typography>
      {!isOwnRecord && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Employee: <strong>{completion.first_name} {completion.last_name}</strong>
        </Typography>
      )}
      {completion.document_description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {completion.document_description}
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1.5 }}>{success}</Alert>}

      {canSignSupervisor && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Supervisor sign-off. {supervisorFieldCount > 0
            ? 'Complete the supervisor fields on the document, then add your signature below.'
            : 'Review the document, then add your signature below.'}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        {canSignEmployee && hasPdfFields && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Review each page. Click checkboxes, signature, initial, and date fields directly on the document.
          </Typography>
        )}
        {pdfBlob ? (
          <TrainingPdfViewer
            pdfBlob={pdfBlob}
            fields={completion.fields || []}
            fieldValues={fieldValues}
            onFieldChange={handleFieldChange}
            readOnly={!isSigning}
            activeRole={activeRole}
            defaultSignName={defaultSignName}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        {hasChecklist && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Checklist</Typography>
            {(completion.items || []).map((item) => (
              <FormControlLabel
                key={item.id}
                control={
                  <Checkbox
                    checked={checkedIds.has(item.id)}
                    disabled={!canSignEmployee}
                    onChange={() => toggleItem(item.id)}
                  />
                }
                label={item.label}
              />
            ))}
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {completion.employee_signed_at && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            Employee signature: <strong>{completion.employee_signature_text}</strong>
            {' '}on {formatDateDisplay(completion.employee_signed_at)}
            {completion.document_effective_date && (
              <> (document version: {formatDateDisplay(completion.document_effective_date)})</>
            )}
          </Typography>
        )}
        {employeeSigUrl && (
          <Box
            component="img"
            src={employeeSigUrl}
            alt="Employee signature"
            sx={{ maxWidth: 280, maxHeight: 100, border: '1px solid', borderColor: 'divider', mb: 1 }}
          />
        )}
        {completion.supervisor_signed_at && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            Supervisor signature: <strong>{completion.supervisor_signature_text}</strong>
            {' '}on {formatDateDisplay(completion.supervisor_signed_at)}
          </Typography>
        )}
        {supervisorSigUrl && (
          <Box
            component="img"
            src={supervisorSigUrl}
            alt="Supervisor signature"
            sx={{ maxWidth: 280, maxHeight: 100, border: '1px solid', borderColor: 'divider', mb: 1 }}
          />
        )}

        {isSigning && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {canSignSupervisor ? 'Supervisor signature' : 'Your signature'}
            </Typography>
            <TrainingSignForm
              user={user}
              disabled={submitting}
              signatureImage={signatureImage}
              onSignatureImageChange={setSignatureImage}
              onSignatureTextChange={setSignatureText}
              padHeight={180}
            />
            <Button
              variant="contained"
              disabled={submitting || !signatureImage}
              onClick={handleSubmit}
              sx={{ mt: 2 }}
            >
              {submitting
                ? 'Submitting…'
                : (canSignSupervisor ? 'Submit sign-off' : 'Submit signature')}
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default EmployeeTrainingDetail;
